<template>
  <div class="banner-carousel-wrapper">
    <div
      class="banner-carousel"
      @mouseenter="pauseAutoplay"
      @mouseleave="resumeAutoplay"
    >
      <div class="carousel-inner">
        <transition name="slide-fade" mode="out-in">
          <div
            :key="currentIndex"
            class="carousel-slide"
          >
            <img
              :src="currentSlide.image"
              :alt="currentSlide.title"
              class="slide-image"
              :class="{
                'slide-image--crop-top': currentSlide.cropTop,
                'slide-image--crop-y': currentSlide.cropY
              }"
            />
            <div class="slide-content" :class="{ 'light-text': currentSlide.lightText, 'dark-text': currentSlide.darkText }">
              <h2 class="slide-title" v-html="formatTitle(currentSlide.title)"></h2>
            </div>
          </div>
        </transition>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'

const props = defineProps({
  slides: {
    type: Array,
    required: true,
    default: () => []
  },
  interval: {
    type: Number,
    default: 3000
  }
})

const currentIndex = ref(0)
const timer = ref(null)
const paused = ref(false)

const currentSlide = computed(() => props.slides[currentIndex.value] || {})

const formatTitle = (title) => {
  return String(title || '')
    .split('|')
    .map((line) => `<span class="title-line">${line.replace(/ /g, '&nbsp;')}</span>`)
    .join('')
}

const stopAutoplay = () => {
  if (timer.value) {
    clearTimeout(timer.value)
    timer.value = null
  }
}

const startAutoplay = () => {
  stopAutoplay()
  if (paused.value || props.slides.length < 2) return
  timer.value = setTimeout(() => {
    currentIndex.value = (currentIndex.value + 1) % props.slides.length
    startAutoplay()
  }, props.interval)
}

const pauseAutoplay = () => {
  paused.value = true
  stopAutoplay()
}

const resumeAutoplay = () => {
  paused.value = false
  startAutoplay()
}

onMounted(() => {
  startAutoplay()
})

onBeforeUnmount(() => {
  stopAutoplay()
})
</script>

<style scoped>
.banner-carousel-wrapper {
  position: relative;
  width: 100%;
}

.banner-carousel {
  position: relative;
  width: 100%;
  overflow: hidden;
  aspect-ratio: 1920 / 700;
  cursor: default;
  /* 以 banner 宽度为容器单位，文字/边距随画面等比例缩放 */
  container-type: inline-size;
  container-name: home-banner;
}

.carousel-inner {
  position: relative;
  width: 100%;
  height: 100%;
}

.carousel-slide {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.slide-fade-enter-active {
  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-fade-leave-active {
  transition: opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-fade-enter-from {
  opacity: 0.3;
  transform: translateX(30px);
}

.slide-fade-leave-to {
  opacity: 0;
}

.slide-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.slide-image--crop-top {
  height: calc(100% + 1cm);
  max-height: none;
  margin-top: -1cm;
}

.slide-image--crop-y {
  height: calc(100% + 1cm);
  max-height: none;
  margin-top: -5mm;
}

.slide-content {
  position: absolute;
  /* 设计稿 1920 宽：left/bottom ≈ 80px → 4.17cqw */
  bottom: 4.17cqw;
  left: 4.17cqw;
  right: auto;
  max-width: 62%;
  z-index: 2;
}

.slide-title {
  /* 设计稿 1920 宽：48px → 2.5cqw，放大屏同步放大，不再封顶 */
  font-size: 2.5cqw;
  font-weight: 600;
  color: #ecf6ff;
  line-height: 1.4;
  margin: 0;
  text-shadow: 0 2px 20px rgba(0, 0, 0, 0.3);
}

.slide-title :deep(.title-line) {
  display: block;
  white-space: nowrap;
}

.slide-content.light-text .slide-title {
  color: #ffffff;
  text-shadow: 0 4px 32px rgba(2, 6, 23, 0.9);
}

.slide-content.dark-text .slide-title {
  color: #0f172a;
  text-shadow: none;
}
</style>
